import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import AuthService from '../../services/AuthService';
import VersionService from '../../services/VersionService';
import { LayoutAppBar } from './LayoutAppBar';
import { LayoutDrawer } from './LayoutDrawer';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { log } from '../../utils/errors/logger';

interface LayoutProps {
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ onLogout }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [versionInfo, setVersionInfo] = useState<{api: string, web: string}>({ api: '...', web: '...' });
  const [adminSectionOpen, setAdminSectionOpen] = useState(true);
  const [superadminSectionOpen, setSuperadminSectionOpen] = useState(true);
  const [user, setUser] = useState(AuthService.getUser());

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const loadVersionInfo = async () => {
      try {
        const versions = await VersionService.getVersionInfo();
        setVersionInfo(versions);
      } catch (error) {
        log.error('Failed to load version information:', { error });
      }
    };
    void loadVersionInfo(); // Explicitly mark as intentionally not awaited
  }, []);

  // Listen for user data changes (e.g., profile updates from Settings)
  useEffect(() => {
    const handleUserDataChanged = () => {
      log.info('User data changed event received in Layout, updating user state');
      setUser(AuthService.getUser());
    };

    window.addEventListener('authStateChanged', handleUserDataChanged);

    return () => {
      window.removeEventListener('authStateChanged', handleUserDataChanged);
    };
  }, []);
  
  const handleNavigation = useCallback((path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [navigate, isMobile]);

  const handleLogout = useCallback(() => {
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const handleMenuClick = useCallback(() => {
    setDrawerOpen(prev => !prev);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleAdminSectionToggle = useCallback(() => {
    setAdminSectionOpen(prev => !prev);
  }, []);

  const handleSuperadminSectionToggle = useCallback(() => {
    setSuperadminSectionOpen(prev => !prev);
  }, []);
  
  return (
    <Box sx={{ display: 'flex' }}>
      <LayoutAppBar
        user={user}
        isMobile={isMobile}
        onMenuClick={handleMenuClick}
        onLogout={handleLogout}
      />

      <LayoutDrawer
        user={user}
        isMobile={isMobile}
        drawerOpen={drawerOpen}
        onClose={handleDrawerClose}
        currentPath={location.pathname}
        onNavigate={handleNavigation}
        onLogout={handleLogout}
        adminSectionOpen={adminSectionOpen}
        superadminSectionOpen={superadminSectionOpen}
        onAdminSectionToggle={handleAdminSectionToggle}
        onSuperadminSectionToggle={handleSuperadminSectionToggle}
        versionInfo={versionInfo}
      />
      
      <Box component="main" sx={{
        flexGrow: 1,
        p: 0,
        width: { md: `calc(100% - 250px)` },
        marginTop: '64px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 64px)', // Full height minus app bar
        overflow: 'auto'
      }}>
        <EmailVerificationBanner user={user} />
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;